import MedicalReportMixin from '@/shared/mixins/medical-report.mixin'
import { mapGetters, mapActions } from 'vuex'
import Dialog from '@/components/dialog/index.vue'
import clearIcon from '@/assets/svg/clear.svg?raw'
import WarningIcon from '@/assets/svg/warning-action.svg?raw'
import GenderExclusiveExams from '@/components/medico/radioReport/gender-exclusive-exams.json'
import LateralityExams from '@/components/medico/radioReport/laterality-exams.json'
import BodyParts from '@/components/medico/radioReport/body-parts.json'
import SpecialtyConflicts from '@/components/medico/radioReport/specialty-conflicts.json'
import ReportKeywords from '@/components/medico/radioReport/report-keywords.json'
import {
  checkLateralityConflict,
  findKeywordsInText,
  findStatusConflict,
  findGenderConflict,
  findBodyPartConflict,
  getConflictingGenderForSpecialty,
  collectSpecialtyKeywords
} from '@/shared/utils/report-validation-checks'
import { stringSimilarity } from 'string-similarity-js'
import CONSTANTS from '@/shared/constants/constants'
import { sanitizeReportText } from '@/shared/utils/sanitize-report-text'

const STORAGE_KEY_SIMILARITY = 'reportSimilarityHistory'

export default {
  name: 'laudar-exame',
  props: {
    isReadOnly: {
      type: Boolean,
      default: false
    },
    examId: {
      type: Number,
      required: true
    },
    exam: {
      type: Object,
      required: true
    },
    isAria: {
      type: Boolean,
      default: false
    },
    isOit: {
      type: Boolean,
      default: false
    }
  },
  mixins: [
    MedicalReportMixin
  ],
  components: {
    Dialog
  },

  watch: {
    exam () {
      this.showGenderConflictDialog = false
      this.showSimilarityDialog = false
      this.resetDivergences()
      this.resetSimilarityState()
      this.resetAlertAuditState()
    }
  },
  mounted () {
    this._onFeedbackPopup = () => {
      this.showGenderConflictDialog = false
      this.showSimilarityDialog = false
      this.resetDivergences()
      this.resetSimilarityState()
    }
    this.$root.$on('showFeedbackPopup', this._onFeedbackPopup)
    this.setSpecialtyConflicts()
    this.setReportKeywords()
  },
  beforeDestroy () {
    this.$root.$off('showFeedbackPopup', this._onFeedbackPopup)
  },
  data () {
    return {
      isEnabled: false,
      isEdited: false,
      laudo: {
        type: Object,
        default: {
          examId: 0,
          description: '',
          status: 0,
          masks: []
        }
      },
      repeat: {
        default: {
          examId: 0,
          description: '',
          reasonsId: []
        }
      },
      clear: clearIcon,
      icon: WarningIcon,
      openDialog: false,
      genderExclusiveExams: GenderExclusiveExams,
      hasGenderConflict: false,
      genderConflictExamGender: '',
      genderConflictKeywords: [],
      lateralityExams: LateralityExams,
      hasLateralityConflict: false,
      lateralityConflictSide: '',
      lateralityConflictKeywords: [],
      bodyParts: BodyParts,
      hasBodyPartConflict: false,
      bodyPartConflictKeywords: [],
      bodyPartExpectedName: '',
      specialtyConflicts: null,
      conflictIndexBySpeciality: {},
      conflictSpecialtyName: '',
      specialtyConflictKeywords: [],
      reportKeywords: null,
      statusChangeRequired: false,
      statusConflictKeywords: [],
      foundKeywords: [],
      userDecisionMade: false,
      lastValidatedText: '',
      activeDivergences: [],
      showGenderConflictDialog: false,
      genderConflictMessage: '',
      showSimilarityDialog: false,
      similarityMatchPercentage: 0,
      MAX_REPORTS_HISTORY: 3,
      SIMILARITY_THRESHOLD: 0.95,
      similarityWarningActive: false,
      lastSimilarityWarnedText: '',
      similarityUserDecisionMade: false,
      pastedText: false,
      pendingCheckResults: null,
      lastTriggeredCheckResults: null,
      alertReviewedFlags: {
        similarity: false,
        gender: false,
        bodyParts: false
      }
    }
  },
  methods: {
    ...mapGetters([
      'getIsLaudar'
    ]),
    ...mapActions([
      'changeClearReport'
    ]),

    async validateLaudar (skipSimilarityCheck = false) {
      await this.$nextTick()
      const shouldSkipSimilarityCheck = skipSimilarityCheck === true
      if (!shouldSkipSimilarityCheck) {
        const shouldShowSimilarityWarning = this.shouldOpenSimilarityDialog()
        if (shouldShowSimilarityWarning) {
          if (!this.similarityMatchPercentage) {
            this.similarityMatchPercentage = this.getSimilarityPercentageForCurrentText()
          }
          const divergences = this.collectDivergences(true)
          if (divergences.length > 0) {
            this.captureTriggeredCheckResults(divergences)
            this.genderConflictMessage = this.buildDivergenceMessage(divergences)
            this.showGenderConflictDialog = true
            this.showSimilarityDialog = true
            return
          }
          this.captureTriggeredCheckResults([{ type: 'similarity' }])
          this.showSimilarityDialog = true
          return
        }
      }
      const allowedSpecialties = [1, 6, 9, 10]
      const specialtyId = Number(this.exam?.categorySpecialtyId)
      const clicouViewer = localStorage.getItem('clicou_xview_ou_pixeon') || 'false'

      if (!allowedSpecialties.includes(specialtyId)) {
        console.warn('⛔️ Especialidade não permitida para rastreamento. GTM ignorado.', specialtyId)
      } else {
        const eventPayload = {
          event: 'click_laudar_tracked',
          click_text: 'Laudar',
          button_id: 'laudar',
          page_path: window.location.pathname,
          specialty_id: specialtyId,
          clicou_xview_ou_pixeon: clicouViewer
        }
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push(eventPayload)
      }

      localStorage.removeItem('clicou_xview_ou_pixeon')

      if (this.userDecisionMade && (this.laudo?.description || '') !== this.lastValidatedText) {
        this.userDecisionMade = false
      }

      const divergences = this.collectDivergences(false)
      if (divergences.length > 0) {
        this.captureTriggeredCheckResults(divergences)
        this.genderConflictMessage = this.buildDivergenceMessage(divergences)
        this.showGenderConflictDialog = true
        return
      }

      if (this.isOit) {
        this.$emit('reloadReport')
      } else {
        this.laudar()
      }
    },
    validateRepeat () {
      if (this.isOit) {
        this.$emit('reloadReport')
      } else {
        this.repeatExam()
      }
    },
    async laudar () {
      try {
        this.$root.$emit('showLoading')
        this.isEdited = false
        const checkResults = this.pendingCheckResults || this.buildCheckResults([], 0, [], this.alertReviewedFlags)
        const report = await this.sendReport({
          ...this.laudo,
          description: sanitizeReportText(this.laudo?.description),
          checkResults
        })
        this.addReportToHistory(this.laudo?.description)
        this.resetSimilarityState()
        this.resetAlertAuditState()
        this.pastedText = false
        const data = {
          hasMoreExams: report.data.existExam,
          type: ''
        }
        localStorage.removeItem('savedReports')
        localStorage.removeItem('savedOITReports')
        this.$emit('reportOk', data)
      } finally {
        this.$root.$emit('hideLoading')
      }
    },
    collectBodyPartsDetected (divergences, bodyPartConflictKeywords) {
      const uniqueDetectedParts = []
      const seenNormalizedParts = new Set()
      const addDetectedPart = (rawValue) => {
        const normalizedPart = (rawValue || '').toString().trim()
        if (!normalizedPart || seenNormalizedParts.has(normalizedPart)) return
        seenNormalizedParts.add(normalizedPart)
        uniqueDetectedParts.push(normalizedPart)
      }
      for (const d of divergences || []) {
        if ((d.type === 'laterality' || d.type === 'specialty' || d.type === 'bodypart') && Array.isArray(d.keywords)) {
          for (const k of d.keywords) {
            if (k && typeof k === 'object' && k.bodyPart && k.word) {
              addDetectedPart(`${k.bodyPart}: ${k.word}`)
            } else {
              addDetectedPart(k.word || k)
            }
          }
        }
      }
      for (const row of bodyPartConflictKeywords || []) {
        if (row.bodyPart && row.word) {
          addDetectedPart(`${row.bodyPart}: ${row.word}`)
        } else {
          addDetectedPart(row.word || row.bodyPart)
        }
      }
      return uniqueDetectedParts
    },
    getAlertTypesFromDivergences (divergences = []) {
      const types = new Set((divergences || []).map((d) => d.type))
      return {
        similarity: types.has('similarity'),
        gender: types.has('gender'),
        bodyParts: types.has('laterality') || types.has('bodypart') || types.has('specialty')
      }
    },
    markAlertsAsReviewed (divergences = []) {
      const alertTypes = this.getAlertTypesFromDivergences(divergences)
      this.alertReviewedFlags = {
        similarity: this.alertReviewedFlags.similarity || alertTypes.similarity,
        gender: this.alertReviewedFlags.gender || alertTypes.gender,
        bodyParts: this.alertReviewedFlags.bodyParts || alertTypes.bodyParts
      }
    },
    markAlertsAsReviewedFromCheckResults (checkResults = {}) {
      this.alertReviewedFlags = {
        similarity: this.alertReviewedFlags.similarity || Boolean(checkResults.similarity?.similarityAlert),
        gender: this.alertReviewedFlags.gender || Boolean(checkResults.gender?.genderAlert),
        bodyParts: this.alertReviewedFlags.bodyParts || Boolean(checkResults.bodyParts?.bodyPartsAlert)
      }
    },
    resetAlertAuditState () {
      this.pendingCheckResults = null
      this.lastTriggeredCheckResults = null
      this.alertReviewedFlags = {
        similarity: false,
        gender: false,
        bodyParts: false
      }
    },
    buildCheckResults (divergences, similarityPercent, bodyPartConflictKeywords, reviewedFlags = {}) {
      const alertTypes = this.getAlertTypesFromDivergences(divergences)
      const history = this.lastTriggeredCheckResults || {}
      const historicalSimilarityAlert = Boolean(history.similarity?.similarityAlert)
      const historicalGenderAlert = Boolean(history.gender?.genderAlert)
      const historicalBodyPartsAlert = Boolean(history.bodyParts?.bodyPartsAlert)
      const historicalPercent = history.similarity?.similarityPercentCheck || 0
      const historicalDetected = Array.isArray(history.bodyParts?.bodyPartsDetected)
        ? history.bodyParts.bodyPartsDetected
        : []
      const currentDetected = alertTypes.bodyParts
        ? this.collectBodyPartsDetected(divergences, bodyPartConflictKeywords)
        : []

      // Após review, preserva os alertas e o contexto histórico para auditoria
      const similarityAlert = alertTypes.similarity || historicalSimilarityAlert
      const genderAlert = alertTypes.gender || historicalGenderAlert
      const bodyPartsAlert = alertTypes.bodyParts || historicalBodyPartsAlert

      return {
        similarity: {
          similarityAlert,
          similarityPercentCheck: similarityAlert
            ? (similarityPercent || historicalPercent || 0)
            : historicalPercent,
          similarityAlertReviewed: Boolean(reviewedFlags.similarity)
        },
        gender: {
          genderAlert,
          genderAlertReviewed: Boolean(reviewedFlags.gender)
        },
        bodyParts: {
          bodyPartsAlert,
          bodyPartsDetected: bodyPartsAlert
            ? (currentDetected.length ? currentDetected : historicalDetected)
            : historicalDetected,
          bodyPartsAlertReviewed: Boolean(reviewedFlags.bodyParts)
        }
      }
    },
    captureTriggeredCheckResults (divergences = this.activeDivergences) {
      this.lastTriggeredCheckResults = this.buildCheckResults(
        divergences,
        this.similarityMatchPercentage,
        this.bodyPartConflictKeywords,
        this.alertReviewedFlags
      )
      return this.lastTriggeredCheckResults
    },
    prepareCheckResultsFromActiveAlerts ({ includeSimilarity = false } = {}) {
      let triggered = this.lastTriggeredCheckResults
      if (!triggered) {
        const divergencesSnapshot = [...(this.activeDivergences || [])]
        if (includeSimilarity && this.similarityWarningActive) {
          const hasSimilarity = divergencesSnapshot.some((d) => d.type === 'similarity')
          if (!hasSimilarity) {
            divergencesSnapshot.push({ type: 'similarity' })
          }
        }
        triggered = this.buildCheckResults(
          divergencesSnapshot,
          this.similarityMatchPercentage,
          this.bodyPartConflictKeywords,
          this.alertReviewedFlags
        )
      } else if (includeSimilarity && this.similarityWarningActive) {
        triggered = {
          ...triggered,
          similarity: {
            ...triggered.similarity,
            similarityAlert: true,
            similarityPercentCheck: this.similarityMatchPercentage || triggered.similarity?.similarityPercentCheck || 0
          }
        }
      }

      // Confirmar envia os alertas ativos; *Reviewed só fica true se veio de um Revisar anterior
      this.pendingCheckResults = {
        similarity: {
          ...triggered.similarity,
          similarityAlertReviewed: Boolean(this.alertReviewedFlags.similarity)
        },
        gender: {
          ...triggered.gender,
          genderAlertReviewed: Boolean(this.alertReviewedFlags.gender)
        },
        bodyParts: {
          ...triggered.bodyParts,
          bodyPartsAlertReviewed: Boolean(this.alertReviewedFlags.bodyParts)
        }
      }
    },
    async repeatExam () {
      try {
        this.$root.$emit('showLoading')
        const {examId, description, reasonsId} = this.repeat
        const repeatExam = await this.examRepeat(examId, description, reasonsId || [])
        const data = {
          hasMoreExams: repeatExam.data.existExam,
          type: ''
        }
        this.$emit('reportOk', data)
      } finally {
        this.$root.$emit('hideLoading')
      }
    },
    normalizeGender (gender) {
      if (!gender) return null
      const g = gender.toString().toUpperCase().trim()
      if (g === 'FEMININO' || g === 'F') return 'F'
      if (g === 'MASCULINO' || g === 'M') return 'M'
      return null
    },
    getCurrentPatientGender () {
      return this.normalizeGender(this.exam?.gender)
    },
    removeDiacritics (text = '') {
      const safeText = String(text)
      try {
        return safeText.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      } catch (error) {
        return safeText
      }
    },
    escapeRegex (text = '') {
      const safeText = String(text)
      return safeText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    capitalize (text = '') {
      return text ? text.charAt(0).toUpperCase() + text.slice(1) : text
    },
    isXrayCategory () {
      return Number(this.exam?.categorySpecialtyId) === CONSTANTS.CATEGORY.XRAY
    },
    canValidateReportSimilarity () {
      return Boolean(this.exam?.enableSimilarityCheck) && !this.isXrayCategory()
    },
    getConflictingGenderForSpecialty (specialityId) {
      return getConflictingGenderForSpecialty({ specialityId, genderExclusiveExams: this.genderExclusiveExams })
    },
    detectGenderDivergence () {
      const reportText = this.laudo?.description || ''
      const patientGender = this.getCurrentPatientGender()
      if (this.userDecisionMade && reportText === this.lastValidatedText) return null

      const conflict = findGenderConflict({
        // Specialty-based detection only applies before the user has decided.
        specialityId: this.userDecisionMade ? null : this.exam?.specialityId,
        patientGender,
        reportText,
        genderExclusiveExams: this.genderExclusiveExams
      })
      if (!conflict.hasConflict) return null

      this.hasGenderConflict = true
      this.genderConflictExamGender = conflict.examGender
      if (conflict.source === 'keyword') this.genderConflictKeywords = conflict.keywords

      const genderLabel = (gender) => gender === 'F'
        ? this.$i18n.t('radio_report.gender_female')
        : this.$i18n.t('radio_report.gender_male')

      if (conflict.source === 'specialty') {
        return {
          type: 'gender',
          intro: this.$i18n.t('radio_report.gender_exam_conflict_intro', {
            examGender: `<b>${genderLabel(conflict.examGender)}</b>`,
            patientGender: `<b>${genderLabel(patientGender)}</b>`
          }),
          keywords: []
        }
      }

      let intro
      if (patientGender && conflict.examGender) {
        intro = this.$i18n.t('radio_report.unified_gender_keyword_intro', {
          examGender: genderLabel(conflict.examGender),
          patientGender: genderLabel(patientGender)
        })
      } else {
        intro = `<b>${this.$i18n.t('radio_report.unified_gender_label')}:</b>`
      }

      return { type: 'gender', intro, keywords: conflict.keywords }
    },
    detectLateralityDivergence () {
      const reportText = this.laudo?.description || ''
      if (this.userDecisionMade && reportText === this.lastValidatedText) return null
      if (!reportText?.trim()) return null

      const specialityId = this.exam?.specialityId
      if (!specialityId) return null

      const result = checkLateralityConflict({
        specialityId,
        reportText,
        lateralityData: this.lateralityExams
      })

      if (!result.hasConflict) return null

      this.hasLateralityConflict = true
      this.lateralityConflictSide = result.examSide
      this.lateralityConflictKeywords = result.conflictKeywords

      const examSideLabel = result.examSide === 'left' ? this.$i18n.t('radio_report.laterality_left') : this.$i18n.t('radio_report.laterality_right')
      const oppositeSideLabel = result.examSide === 'left' ? this.$i18n.t('radio_report.laterality_right') : this.$i18n.t('radio_report.laterality_left')
      return {
        type: 'laterality',
        intro: this.$i18n.t('radio_report.unified_laterality_conflict_intro', { examSide: `<b>${examSideLabel}</b>`, oppositeSide: `<b>${oppositeSideLabel}</b>` }),
        keywords: result.conflictKeywords
      }
    },
    detectBodyPartDivergence () {
      const reportText = this.laudo?.description || ''
      if (this.userDecisionMade && reportText === this.lastValidatedText) return null

      const examName = this.exam?.specialtyName || this.exam?.specialtyFullName || ''
      const conflict = findBodyPartConflict({
        examName,
        reportText,
        specialityId: this.exam?.specialityId,
        bodyPartsData: this.bodyParts
      })
      if (!conflict.hasConflict) return null

      this.bodyPartExpectedName = conflict.expectedBases.map((b) => this.capitalize(b)).join(', ')
      this.hasBodyPartConflict = true
      this.bodyPartConflictKeywords = conflict.conflictKeywords

      return {
        type: 'bodypart',
        intro: this.$i18n.t('radio_report.unified_bodypart_conflict_intro', { expected: `<b>${this.bodyPartExpectedName}</b>` }),
        keywords: conflict.conflictKeywords
      }
    },
    setSpecialtyConflicts () {
      this.specialtyConflicts = SpecialtyConflicts
      const conflictIndexBySpecialityId = {}
      const conflictListBySpecialityId = this.specialtyConflicts?.pt?.conflicts

      if (Array.isArray(conflictListBySpecialityId)) {
        for (const conflictConfig of conflictListBySpecialityId) {
          if (!conflictConfig || typeof conflictConfig.specialityId !== 'number') continue
          conflictIndexBySpecialityId[conflictConfig.specialityId] = conflictConfig
        }
      }
      this.conflictIndexBySpeciality = conflictIndexBySpecialityId
    },
    collectKeywordsForSpecialty (specialityId) {
      return collectSpecialtyKeywords({ specialtyConflicts: this.specialtyConflicts, specialityId })
    },
    detectSpecialtyDivergence (lateralityDiv, bodyPartDiv) {
      const reportText = this.laudo?.description || ''
      if (this.userDecisionMade && reportText === this.lastValidatedText) return null
      if (!reportText?.trim() || !this.specialtyConflicts) return null

      const examSpecialityId = this.exam?.specialityId
      if (!examSpecialityId) return null

      const { keywords: conflictKeywords, name: conflictSpecialtyName } =
        this.collectKeywordsForSpecialty(examSpecialityId)
      if (!conflictKeywords?.length) return null

      const keywordMatches = findKeywordsInText(reportText, conflictKeywords, {
        specialityId: examSpecialityId,
        bodyPartsData: this.bodyParts
      })
      if (!keywordMatches.length) return null

      const lateralityWords = new Set((lateralityDiv?.keywords || []).map(k => (k.word || '').toLowerCase()))
      const bodyPartWords = new Set((bodyPartDiv?.keywords || []).map(k => (k.word || '').toLowerCase()))
      const filtered = keywordMatches.filter(k => !lateralityWords.has(k.word.toLowerCase()) && !bodyPartWords.has(k.word.toLowerCase()))
      if (!filtered.length) return null

      this.specialtyConflictKeywords = filtered
      this.conflictSpecialtyName = conflictSpecialtyName || ''

      return {
        type: 'specialty',
        intro: this.$i18n.t('radio_report.unified_specialty_conflict_intro', { specialty: `<b>${this.conflictSpecialtyName}</b>` }),
        keywords: filtered
      }
    },
    setReportKeywords () {
      const currentLang = this.$i18n?.locale?.substring(0, 2) || 'pt'
      this.reportKeywords = ReportKeywords[currentLang] || ReportKeywords.pt
    },
    detectStatusDivergence () {
      const reportText = this.laudo?.description || ''
      if (!reportText?.trim()) return null
      if (this.userDecisionMade && reportText === this.lastValidatedText) return null
      if (!this.reportKeywords) return null

      const conflict = findStatusConflict({
        reportText,
        status: this.laudo.status,
        reportKeywords: this.reportKeywords,
        normalStatusValue: 1,
        abnormalStatusValue: 2
      })
      if (!conflict) return null

      this.statusChangeRequired = true
      this.statusConflictKeywords = conflict.keywords
      this.foundKeywords = conflict.keywords

      const introKey = conflict.conflictType === 'normal_status_with_abnormal_text'
        ? 'radio_report.normal_status_with_abnormal_text_intro'
        : 'radio_report.abnormal_status_with_normal_text_intro'

      return {
        type: 'status',
        intro: this.$i18n.t(introKey),
        keywords: conflict.keywords
      }
    },
    collectDivergences (includeSimilarity = false) {
      this.resetDivergences()

      const statusDiv = this.detectStatusDivergence()
      const genderDiv = this.detectGenderDivergence()
      const lateralityDiv = this.detectLateralityDivergence()
      const bodyPartDiv = this.detectBodyPartDivergence()
      const specialtyDiv = this.detectSpecialtyDivergence(lateralityDiv, bodyPartDiv)

      const divergences = [statusDiv, genderDiv, lateralityDiv, bodyPartDiv, specialtyDiv].filter(Boolean)

      if (includeSimilarity && this.similarityWarningActive) {
        const percent = this.similarityMatchPercentage || 0
        divergences.push({
          type: 'similarity',
          intro: `<span style="display:block;text-align:center;"><b>${this.$i18n.t('radio_report.unified_similarity_label')}:</b> ${this.$i18n.t('radio_report.unified_similarity_detail', { percentage: percent })}</span>`,
          keywords: []
        })
      }

      this.activeDivergences = divergences
      return divergences
    },
    buildDivergenceMessage (divergences) {
      if (!divergences?.length) return ''

      const sections = divergences.map(d => {
        let html = ''
        if (d.intro) html += `<p style="margin:0 0 4px;">${d.intro}</p>`
        if (d.keywords?.length) {
          const items = d.keywords.map(k => `<li><b>${this.capitalize(k.word)}</b></li>`).join('')
          html += `<ul style="margin:4px 0 0;">${items}</ul>`
        }
        return `<div style="margin-bottom:10px;">${html}</div>`
      })

      const sep = '<hr style="border:0;border-top:1px solid #eee;margin:8px 0;">'
      const intro = divergences.length > 1
        ? `<p style="font-size:1.05rem;color:#555;margin-bottom:10px;">${this.$i18n.t('radio_report.unified_divergences_intro')}</p>`
        : ''
      const scrollStyle = divergences.length > 2 ? 'max-height:280px;overflow-y:auto;padding-right:4px;' : ''
      const body = `<div style="text-align:left;${scrollStyle}">${intro}${sections.join(sep)}</div>`
      const question = `<p style="font-size:1.05rem;margin-top:14px;font-weight:500;color:#333;">${this.$i18n.t('radio_report.review_text_question')}</p>`

      return `${body}${question}`
    },
    resetDivergences () {
      this.activeDivergences = []
      this.statusChangeRequired = false
      this.statusConflictKeywords = []
      this.foundKeywords = []
      this.hasGenderConflict = false
      this.genderConflictExamGender = ''
      this.genderConflictKeywords = []
      this.hasLateralityConflict = false
      this.lateralityConflictSide = ''
      this.lateralityConflictKeywords = []
      this.hasBodyPartConflict = false
      this.bodyPartConflictKeywords = []
      this.bodyPartExpectedName = ''
      this.specialtyConflictKeywords = []
      this.conflictSpecialtyName = ''
    },
    confirmGenderConflict () {
      if (!this.isOit) {
        this.prepareCheckResultsFromActiveAlerts()
      }
      this.userDecisionMade = true
      this.lastValidatedText = this.laudo?.description || ''
      this.showGenderConflictDialog = false
      this.resetDivergences()

      if (this.isOit) {
        this.$emit('reloadReport')
      } else {
        this.laudar()
      }
    },
    closeGenderConflictDialog () {
      if (!this.lastTriggeredCheckResults && this.activeDivergences?.length) {
        this.captureTriggeredCheckResults(this.activeDivergences)
      }
      if (this.lastTriggeredCheckResults) {
        this.markAlertsAsReviewedFromCheckResults(this.lastTriggeredCheckResults)
      } else {
        this.markAlertsAsReviewed(this.activeDivergences)
      }
      // Mantém lastTriggeredCheckResults (percentual e bodyPartsDetected) para auditoria no envio
      this.pendingCheckResults = null
      this.showGenderConflictDialog = false
      this.resetDivergences()
    },
    isButtonEnabled () {
      if (!this.laudo || (this.laudo && this.laudo.description && (
        this.laudo.description.length < 20 ||
        this.laudo.description.length > 10000 ||
        !this.laudo.description.trim()))) {
        return false
      }
      return this.laudo.description && this.laudo.status
    },
    setLaudo (description, status, masksIds) {
      this.handleSimilarityTextChange(description)
      this.laudo = {
        examId: this.examId,
        description,
        status,
        masksIds,
        edited: this.isEdited,
        reportCorrectionNotice: this.exam.reportCorrectionNotice || null
      }
    },
    setRepeat (description, reasonsId) {
      this.repeat = {
        examId: this.examId,
        description,
        reasonsId
      }
    },
    normalizeReportText (text = '') {
      return String(text)
        .replace(/\r\n|\r/g, '\n')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n+/g, '\n')
        .trim()
    },
    getReportHistory () {
      try {
        const storedValue = localStorage.getItem(STORAGE_KEY_SIMILARITY)
        const parsedValue = JSON.parse(storedValue || '[]')
        return Array.isArray(parsedValue) ? parsedValue : []
      } catch (error) {
        return []
      }
    },
    saveReportHistory (historyList = []) {
      localStorage.setItem(STORAGE_KEY_SIMILARITY, JSON.stringify(historyList))
    },
    addReportToHistory (reportText = '') {
      if (this.isOit) return
      if (!this.canValidateReportSimilarity()) return

      const normalizedText = this.normalizeReportText(reportText)
      if (!normalizedText) return

      const currentHistory = this.getReportHistory()
      const updatedHistory = [normalizedText, ...currentHistory].slice(0, this.MAX_REPORTS_HISTORY)
      this.saveReportHistory(updatedHistory)
    },
    calculateSimilarityScore (textA = '', textB = '') {
      const normalizedA = this.normalizeReportText(textA)
      const normalizedB = this.normalizeReportText(textB)

      if (!normalizedA && !normalizedB) return 1
      if (!normalizedA || !normalizedB) return 0

      return stringSimilarity(normalizedA, normalizedB)
    },
    getSimilarityPercentageForCurrentText () {
      const normalizedReportText = this.normalizeReportText(this.laudo?.description)
      if (!normalizedReportText) return 0

      const reportHistoryList = this.getReportHistory()
      if (!reportHistoryList.length) return 0

      let highestSimilarityScore = 0
      for (const savedReportText of reportHistoryList) {
        const similarityScore = this.calculateSimilarityScore(normalizedReportText, savedReportText)
        if (similarityScore > highestSimilarityScore) {
          highestSimilarityScore = similarityScore
        }
      }
      return Math.round(highestSimilarityScore * 100)
    },
    resetSimilarityState () {
      this.similarityMatchPercentage = 0
      this.similarityWarningActive = false
      this.similarityUserDecisionMade = false
      this.lastSimilarityWarnedText = ''
      this.showSimilarityDialog = false
      this.pastedText = false
    },
    setPastedText () {
      this.pastedText = true
      this.similarityUserDecisionMade = false
    },
    handleSimilarityTextChange (text = '') {
      const normalizedText = this.normalizeReportText(text)
      if ((this.similarityWarningActive || this.similarityUserDecisionMade) && normalizedText !== this.lastSimilarityWarnedText) {
        this.similarityWarningActive = false
        this.similarityUserDecisionMade = false
        this.lastSimilarityWarnedText = ''
        this.similarityMatchPercentage = 0
        this.showSimilarityDialog = false
      }
    },
    shouldOpenSimilarityDialog () {
      if (!this.canValidateReportSimilarity()) {
        this.resetSimilarityState()
        return false
      }

      if (!this.pastedText) return false

      const normalizedReportText = this.normalizeReportText(this.laudo?.description)
      if (!normalizedReportText) {
        this.showSimilarityDialog = false
        this.similarityMatchPercentage = 0
        return false
      }

      if (this.similarityUserDecisionMade && normalizedReportText === this.lastSimilarityWarnedText) {
        return false
      }

      const reportHistoryList = this.getReportHistory()
      if (!reportHistoryList.length) {
        this.showSimilarityDialog = false
        this.similarityMatchPercentage = 0
        return false
      }

      let highestSimilarityScore = 0
      for (const savedReportText of reportHistoryList) {
        const similarityScore = this.calculateSimilarityScore(normalizedReportText, savedReportText)
        if (similarityScore > highestSimilarityScore) {
          highestSimilarityScore = similarityScore
        }
      }
      if (highestSimilarityScore >= this.SIMILARITY_THRESHOLD) {
        this.similarityMatchPercentage = Math.round(highestSimilarityScore * 100)
        this.similarityWarningActive = true
        this.lastSimilarityWarnedText = normalizedReportText
        return true
      }

      this.similarityWarningActive = false
      this.lastSimilarityWarnedText = ''
      this.similarityMatchPercentage = 0
      this.showSimilarityDialog = false
      return false
    },
    reviewSimilarity () {
      if (!this.lastTriggeredCheckResults) {
        this.captureTriggeredCheckResults([{ type: 'similarity' }])
      }
      this.markAlertsAsReviewed([{ type: 'similarity' }])
      // Mantém lastTriggeredCheckResults (similarityPercentCheck) para auditoria no envio
      this.pendingCheckResults = null
      this.similarityWarningActive = false
      this.similarityUserDecisionMade = false
      this.lastSimilarityWarnedText = ''
      this.showSimilarityDialog = false
    },
    confirmSimilarity () {
      this.prepareCheckResultsFromActiveAlerts({ includeSimilarity: true })
      this.similarityWarningActive = false
      this.similarityUserDecisionMade = true
      this.lastSimilarityWarnedText = this.normalizeReportText(this.laudo?.description)
      this.showSimilarityDialog = false
      this.validateLaudar(true)
    },
    confirmCombinedConflict () {
      this.prepareCheckResultsFromActiveAlerts({ includeSimilarity: true })
      this.userDecisionMade = true
      this.lastValidatedText = this.laudo?.description || ''
      this.similarityWarningActive = false
      this.similarityUserDecisionMade = true
      this.lastSimilarityWarnedText = this.normalizeReportText(this.laudo?.description)
      this.showGenderConflictDialog = false
      this.showSimilarityDialog = false
      this.resetDivergences()
      this.validateLaudar(true)
    },
    reviewCombinedConflict () {
      if (!this.lastTriggeredCheckResults && this.activeDivergences?.length) {
        this.captureTriggeredCheckResults([
          ...(this.activeDivergences || []),
          { type: 'similarity' }
        ])
      }
      this.markAlertsAsReviewed([
        ...(this.activeDivergences || []),
        { type: 'similarity' }
      ])
      // Mantém lastTriggeredCheckResults para auditoria no envio
      this.pendingCheckResults = null
      this.similarityWarningActive = true
      this.similarityUserDecisionMade = false
      this.lastSimilarityWarnedText = this.normalizeReportText(this.laudo?.description)
      this.showSimilarityDialog = false
      this.closeGenderConflictDialog()
    },
    handleConflictReview () {
      if (this.activeConflictDialog === 'combined') {
        this.reviewCombinedConflict()
        return
      }
      if (this.activeConflictDialog === 'similarity') {
        this.reviewSimilarity()
        return
      }
      this.closeGenderConflictDialog()
    },
    handleConflictConfirm () {
      if (this.activeConflictDialog === 'combined') {
        this.confirmCombinedConflict()
        return
      }
      if (this.activeConflictDialog === 'similarity') {
        this.confirmSimilarity()
        return
      }
      this.confirmGenderConflict()
    },
    rectify () {
      this.isEdited = true
      this.$emit('clickRectify')
    }
  },
  computed: {
    isLaudar () {
      return this.getIsLaudar()
    },
    isConflictDialogOpen: {
      get () {
        return this.showGenderConflictDialog || this.showSimilarityDialog
      },
      set (value) {
        if (!value) {
          this.showGenderConflictDialog = false
          this.showSimilarityDialog = false
        }
      }
    },
    isCombinedConflict () {
      return this.showGenderConflictDialog && this.showSimilarityDialog
    },
    activeConflictDialog () {
      if (this.isCombinedConflict) return 'combined'
      if (this.showSimilarityDialog) return 'similarity'
      if (this.showGenderConflictDialog) return 'gender'
      return ''
    },
    conflictDialogTitle () {
      return this.$t('radio_report.attention')
    },
    conflictDialogMessage () {
      if (this.activeConflictDialog === 'combined') {
        if (this.activeDivergences.length > 0) {
          return this.buildDivergenceMessage(this.activeDivergences)
        }
        return this.similarityAlertMessage
      }
      if (this.activeConflictDialog === 'similarity') {
        return this.similarityAlertMessage
      }
      return this.genderConflictMessage
    },
    conflictDialogReviewText () {
      return this.$t('radio_report.review_button')
    },
    conflictDialogConfirmText () {
      if (this.activeConflictDialog === 'similarity') {
        return this.$t('radio_report.confirm')
      }
      if (this.activeConflictDialog === 'combined') {
        return this.$t('radio_report.send_report_anyway')
      }
      return this.$t('radio_report.send_report_anyway')
    },
    similarityAlertMessage () {
      const percent = this.similarityMatchPercentage || this.getSimilarityPercentageForCurrentText() || 0
      return this.$t('similarity_alert_message', { percentage: percent })
    }
  }
}
